import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  AnalyticsControllerService,
  AnalyticsForGraphics
} from "../../../services/analytics-controller.service";
import { FiltersData } from "../../../services/filters.service";
import { DatePipe } from "@angular/common";
import { NotificationService } from "../../../services/notification.service";
import { NavigationNoticeService } from "../../../services/navigation-notice.service";
import { RecordRoute } from "../../../services/record-route";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { IDatesDTO, LineGraphDataDTO } from "psa-tnt-models";
import {
  FilterUrlSyncService,
  HydratedValues
} from "../../../services/filter-url-sync.service";
import {
  AnalyticsFilterConfig,
  ANALYTICS_ALERT_TYPE_DEFAULT,
  ANALYTICS_ALERT_TYPES,
  ANALYTICS_CONTAINER_CATEGORIES,
  ANALYTICS_CONTAINER_CATEGORY_DEFAULT,
  ANALYTICS_GRAPH_SLUG_TO_NAME,
  ANALYTICS_GRAPH_NAME_TO_SLUG,
  analyticsConfigFor,
  analyticsUnusedParams
} from "../../../services/filter-sync-configs";
import { IGraph, graphsData } from "./graph-config";
import { BarGraphDataDTO } from "psa-tnt-models";
import { HeaderAnalyticsComponent } from "./header-analytics/header-analytics.component";
import { GraphLibraryComponent } from "./graph-library/graph-library.component";
import { GraphsDisplayerComponent } from "./graphs-displayer/graphs-displayer.component";

/**
 * The filter values a `/analytics/:graphSlug` URL carries, typed off the config
 * that declared them — `range` is a range, `span` a number, `sites` a string
 * list, and the compiler knows it, so nothing here needs a cast.
 *
 * `Partial` because the config is the tab superset while the URL only ever
 * carries the subset THIS chart renders a control for (`analyticsConfigFor`):
 * every key an absent control would have filled is genuinely missing, which is
 * why each one is read behind an `!== undefined` guard.
 */
type AnalyticsUrlValues = Partial<HydratedValues<AnalyticsFilterConfig>>;

/** The four data-derived multiselects, whose selection the URL seeds. */
type SeededParamKey = "sites" | "dep" | "arr" | "pack";

@Component({
  selector: "app-analytics",
  templateUrl: "./analytics.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./analytics.component.scss"],
  imports: [
    HeaderAnalyticsComponent,
    GraphLibraryComponent,
    GraphsDisplayerComponent,
    TranslateModule
  ]
})
export class AnalyticsComponent implements OnInit, OnDestroy {
  private readonly notifier: NotificationService;
  private destroyRequestObservable = new Subject<void>();
  private routeSub?: Subscription;
  /** The `/analytics/:graphSlug` segment: which chart is on screen. */
  private chartRoute: RecordRoute;

  public datas: AnalyticsForGraphics[] = [];
  public loading: boolean = false;
  public hasData: boolean = false;
  public hasError: boolean = false;

  public errorMsg: string = "";
  public selectedGraphConfig: IGraph;
  public configs: IGraph[] = graphsData;

  // Navigation state management
  public showGraphLibrary: boolean = true;
  public isGraphSelected: boolean = false;
  public searchTerm: string = "";
  public filteredCharts: IGraph[] = [];

  public filterGraphic: string = "";
  public filterContainers: string[] = [];

  public todayDate: Date = new Date();
  public filterDateEnd: Date = new Date();
  public filterDateStart: Date = new Date();

  public filterSites: string[] = [];
  public filterDepartureSites: string[] = [];
  public filterArrivalSites: string[] = [];
  public filterDays: number | undefined = undefined;
  public filterAlertType: string = ANALYTICS_ALERT_TYPE_DEFAULT;
  public filterContainerCategory: string = ANALYTICS_CONTAINER_CATEGORY_DEFAULT;
  public filterPackaging: string[] = [];

  // Inputs for the filters depending on the graph-data from backend request
  public filterSitesInput: FiltersData[] = [];
  public filterDepartureSitesInput: FiltersData[] = [];
  public filterArrivalSitesInput: FiltersData[] = [];
  public filterPackagingInput: FiltersData[] = [];

  /**
   * Selections the URL asked for on the four data-derived filters
   * (`sites`/`dep`/`arr`/`pack`). Their option lists only exist once a response
   * is parsed, and `extractFilterInputs` overwrites the selection with a computed
   * default, so the URL's value waits here and is applied there instead — once. A
   * later recompute (alert-type change, refetch) legitimately falls back to that
   * computed default.
   */
  private pendingUrlSelection: { [paramKey: string]: string[] } = {};

  // `readonly`: the option lists are the config's, not the page's to mutate —
  // which is what lets them be the very arrays the URL validates against.
  public alertTypesInput: readonly string[] = ANALYTICS_ALERT_TYPES;
  public containerCategoriesInput: readonly string[] =
    ANALYTICS_CONTAINER_CATEGORIES;

  constructor(
    private analyticsService: AnalyticsControllerService,
    private datePipe: DatePipe,
    private translate: TranslateService,
    private notifierService: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private filterSync: FilterUrlSyncService,
    private navigationNotice: NavigationNoticeService
  ) {
    this.notifier = notifierService;
    // A chart is this page's record: the slug says which one is displayed, and
    // the library view is its list. The filter params carry across every hop —
    // each chart drops the ones it does not declare when it is selected.
    this.chartRoute = new RecordRoute(
      this.route,
      this.router,
      this.navigationNotice,
      {
        paramKey: "graphSlug",
        basePath: ["/analytics"],
        open: (slug: string) => this.showGraphFromSlug(slug),
        close: () => {
          if (this.filterGraphic) {
            this.applyLibraryView();
          }
        },
        queryParamsHandling: "merge"
      }
    );
  }

  public ngOnInit() {
    // Resolve the deep-linked chart FIRST: analytics is one route per chart and
    // each chart declares its own filter set, so which params to hydrate — and
    // which to leave alone — depends on it. Hydrating the whole tab superset here
    // would CLEAR the backend of every param this chart does not carry, deleting
    // `localStorage.filterContainers` on a chart with no `pkg` and silently
    // dropping the user's container filter for kpi/map/list.
    const initialConfig = this.graphFromSlug(this.chartRoute.currentId());

    // URL params win on load: `pkg` is written to localStorage here, the
    // component-local filters are applied after initFilters sets their defaults.
    const urlValues = this.filterSync.hydrateFromUrl(
      this.route,
      analyticsConfigFor(initialConfig?.frontConfig)
    );
    this.initFilters();
    this.applyUrlFilters(urlValues);
    this.initFilteredCharts();

    // Select the deep-linked graph (if any) without clearing the URL's filters;
    // that publishes the chart's whole filter set back to the URL, empty ones
    // included (`?cat=all&span=&sites=…`), so the address bar is the editable
    // form of what is displayed. Then react to later /analytics/:graphSlug
    // changes (back/forward, in-app nav).
    if (initialConfig) {
      this.applyGraphSelection(initialConfig, true);
    }
    this.routeSub = this.chartRoute.follow();

    this.translate.onLangChange
      .pipe(takeUntil(this.destroyRequestObservable))
      .subscribe(() => {
        if (this.searchTerm) {
          this.filterCharts();
        }
      });
  }

  public ngOnDestroy() {
    this.routeSub?.unsubscribe();
    this.destroyRequestObservable.next();
    this.destroyRequestObservable.complete();
  }

  /** The chart a `/analytics/:graphSlug` segment addresses, if it names one. */
  private graphFromSlug(slug: string | null): IGraph | undefined {
    const name = slug ? ANALYTICS_GRAPH_SLUG_TO_NAME[slug] : undefined;
    return name ? this.configs.find((e) => e.name === name) : undefined;
  }

  /** Apply the URL's filter values over the defaults initFilters just set. */
  private applyUrlFilters(urlValues: AnalyticsUrlValues): void {
    // `cat` and `alert` are radios: the config declares their base value, so a
    // declared one always decodes to an offered option. The key is absent from
    // `urlValues` entirely when this chart renders no such radio — the field then
    // keeps the default it was initialised with.
    if (urlValues["cat"] !== undefined) {
      this.filterContainerCategory = urlValues["cat"];
    }
    if (urlValues["alert"] !== undefined) {
      this.filterAlertType = urlValues["alert"];
    }
    if (urlValues["span"] !== undefined) {
      this.filterDays = urlValues["span"];
    }
    const range = urlValues["range"];
    if (range) {
      if (range.start) this.filterDateStart = new Date(range.start);
      if (range.end) this.filterDateEnd = new Date(range.end);
    }

    this.seedUrlSelection(urlValues, "sites", (v) => (this.filterSites = v));
    this.seedUrlSelection(
      urlValues,
      "dep",
      (v) => (this.filterDepartureSites = v)
    );
    this.seedUrlSelection(
      urlValues,
      "arr",
      (v) => (this.filterArrivalSites = v)
    );
    this.seedUrlSelection(urlValues, "pack", (v) => (this.filterPackaging = v));
  }

  /**
   * Apply a URL selection to its field now AND latch it for
   * `extractFilterInputs`. Both are needed: the field has to hold the value
   * before the first mirror (which runs before any data has landed, and would
   * otherwise publish an empty `sites=` and lose the deep-linked selection), and
   * the latch is what survives the computed default the response brings. An empty
   * value is not latched — it means "no filter", which is exactly what the
   * computed default replaces.
   */
  private seedUrlSelection(
    urlValues: AnalyticsUrlValues,
    paramKey: SeededParamKey,
    apply: (values: string[]) => void
  ): void {
    const wanted = urlValues[paramKey];
    if (!wanted?.length) return;
    this.pendingUrlSelection[paramKey] = [...wanted];
    apply([...wanted]);
  }

  /**
   * The pending URL selection for a data-derived filter, narrowed to the options
   * the freshly parsed response actually offers — a stale link must not select a
   * site the data no longer has. Consumed on read; `undefined` (nothing pending,
   * or nothing left of it) means the caller keeps its computed default.
   */
  private takeUrlSelection(
    paramKey: string,
    options: FiltersData[]
  ): string[] | undefined {
    const wanted = this.pendingUrlSelection[paramKey];
    if (!wanted) return undefined;
    delete this.pendingUrlSelection[paramKey];
    const offered = new Set(options.map((option) => String(option.key)));
    const kept = wanted.filter((value) => offered.has(value));
    return kept.length > 0 ? kept : undefined;
  }

  /**
   * Mirror the current filters onto the URL — the subset THIS chart displays a
   * control for, and nothing else: `analyticsUnusedParams` drops the keys the
   * previous chart carried. Passing every value is harmless, the service ignores
   * the ones outside the config.
   */
  private mirrorQueryFilters(): void {
    const frontConfig = this.selectedGraphConfig?.frontConfig;
    // The library view renders no filter control: leave the query string as it
    // arrived (a tab link may have carried filters in for the next chart).
    if (!frontConfig) return;

    this.filterSync.mirrorToUrl(
      this.route,
      analyticsConfigFor(frontConfig),
      {
        pkg: this.filterContainers,
        cat: this.filterContainerCategory,
        range: {
          start: this.filterDateStart?.getTime(),
          end: this.filterDateEnd?.getTime()
        },
        alert: this.filterAlertType,
        span: this.filterDays,
        sites: this.filterSites,
        dep: this.filterDepartureSites,
        arr: this.filterArrivalSites,
        pack: this.filterPackaging
      },
      analyticsUnusedParams(frontConfig)
    );
  }

  private initDatesFilter(): void {
    // Set Today to hours, minutes & seconds 0
    this.todayDate.setHours(0, 0, 0, 0);

    // Set End Date to current date
    this.filterDateEnd = new Date(this.todayDate);
    this.filterDateEnd.setDate(this.todayDate.getDate() - 1);

    // Set Start Date at 2 month ago
    this.filterDateStart = new Date(this.todayDate);
    this.filterDateStart.setMonth(this.todayDate.getMonth() - 2);
  }

  private initFilters(): void {
    // Initialize container filter
    const storedContainers = localStorage.getItem("filterContainers");
    if (storedContainers !== null) {
      this.filterContainers = JSON.parse(storedContainers);
    }

    // Initialize graphic filter
    if (this.filterGraphic) {
      this.selectedGraphConfig = this.configs.find(
        (e) => e.name === this.filterGraphic
      );
    }
    this.initDatesFilter();
  }

  private initFilteredCharts(): void {
    this.filteredCharts = [...this.configs];
  }

  public formatDateGMT(): IDatesDTO {
    const offsetMinutes: number = this.todayDate.getTimezoneOffset();
    const offsetSeconds: number = offsetMinutes * 60;

    return {
      start: Math.round(this.filterDateStart.getTime() / 1000) - offsetSeconds,
      end: Math.round(this.filterDateEnd.getTime() / 1000) - offsetSeconds
    };
  }

  private async getDatas(
    requestName: string,
    requestId: number
  ): Promise<AnalyticsForGraphics> {
    const formattedDates: IDatesDTO = this.formatDateGMT();
    //? Get the label for the name on the
    const label: string = this.translate.instant(
      `analytics.navigator.${this.selectedGraphConfig.name}.cardTitle`
    );

    return new Promise((resolve, reject) => {
      // Cancel the previous request
      this.destroyRequestObservable.next();

      // Launch the new request
      this.analyticsService
        .generateAnalyticsData(
          requestName,
          requestId,
          this.selectedGraphConfig,
          {
            containers: this.filterContainers,
            dates: formattedDates,
            days: this.filterDays,
            isPolyvalent: { polyvalent: true, specific: false, all: undefined }[
              this.filterContainerCategory
            ]
          }
        )
        .pipe(takeUntil(this.destroyRequestObservable))
        .subscribe(
          (result) => {
            resolve(result);
          },
          (error) => {
            this.translate
              .get("analytics.error", {
                dataType: label
              })
              .subscribe((message) => {
                this.errorMsg = message;
              });

            reject(error);
          }
        );
    });
  }

  private clearOptionalFilters(): void {
    this.filterSites = [];
    this.filterDepartureSites = [];
    this.filterArrivalSites = [];
    this.filterDays = undefined;
    this.filterAlertType = ANALYTICS_ALERT_TYPE_DEFAULT;
    this.filterContainerCategory = ANALYTICS_CONTAINER_CATEGORY_DEFAULT;
    this.filterPackaging = [];
    // A chart switch discards this chart's filters, the URL's not-yet-applied
    // ones included — they were addressed to the chart being left.
    this.pendingUrlSelection = {};
  }

  private clearFilterInputs(): void {
    this.filterSitesInput = [];
    this.filterDepartureSitesInput = [];
    this.filterArrivalSitesInput = [];
    this.filterPackagingInput = [];
  }

  /**
   * This method is used to populate the optional filter inputs for the graphs
   * from the data.
   */
  private extractFilterInputs(data: BarGraphDataDTO | LineGraphDataDTO): void {
    if (this.selectedGraphConfig?.frontConfig?.sites && data.type === "bar") {
      //? Extract sites with their values based on current alert type filter
      const dataWithIsRed = data as BarGraphDataDTO & { isRed?: boolean[] };
      const siteValuesMap = new Map<
        string,
        { label: string; totalValue: number }
      >();

      for (let i = 0; i < data.x.length; i++) {
        const site = data.x[i];
        const value = data.y[i];
        const label = data.x_labels[i];
        const isRed = dataWithIsRed.isRed ? dataWithIsRed.isRed[i] : true;

        // Filter based on current alertType setting
        let shouldInclude = true;
        if (this.filterAlertType === "alert" && !isRed) {
          shouldInclude = false; // Skip orange values when filtering for alerts only
        } else if (this.filterAlertType === "warning" && isRed) {
          shouldInclude = false; // Skip red values when filtering for warnings only
        }

        if (shouldInclude) {
          if (siteValuesMap.has(site)) {
            // Sum values for duplicate sites
            const existing = siteValuesMap.get(site)!;
            siteValuesMap.set(site, {
              label: existing.label,
              totalValue: existing.totalValue + value
            });
          } else {
            siteValuesMap.set(site, { label, totalValue: value });
          }
        }
      }

      //? Sort sites by filtered value (descending), then alphabetically
      const inputSites: FiltersData[] = Array.from(siteValuesMap.entries())
        .filter(([site]) => site !== "UNKNOWN") //TODO use a constant
        .sort(
          (a, b) =>
            b[1].totalValue - a[1].totalValue || a[0].localeCompare(b[0])
        )
        .map(
          ([site, data]) =>
            ({
              key: site,
              keyDisplayed: `${site} - ${data.label}`
            }) as FiltersData
        );

      this.filterSitesInput.push(...inputSites);
      //? What the URL asked for, else only the top 10 sites by filtered value
      const newFilterSites =
        this.takeUrlSelection("sites", inputSites) ??
        inputSites.slice(0, 10).map((site) => String(site.key));

      // Create new array reference to trigger Angular change detection
      this.filterSites = [...newFilterSites];
    } else if (
      this.selectedGraphConfig?.frontConfig?.sites &&
      data.type === "line"
    ) {
      const inputSites: FiltersData[] = data.lines.map(
        (line) =>
          ({
            key: line.site,
            keyDisplayed: `${line.site} - ${line.site_label}`
          }) as FiltersData
      );
      this.filterSitesInput.push(...inputSites);
      //? What the URL asked for, else the first 10 sites minus site "MOVING"
      this.filterSites =
        this.takeUrlSelection("sites", inputSites) ??
        inputSites
          .filter((site) => site.key !== "MOVING")
          .slice(0, 10)
          .map((site) => String(site.key));
    }
    if (
      this.selectedGraphConfig?.frontConfig?.departureSites &&
      data.type === "bar"
    ) {
      // Extract departure sites and remove duplicates by key
      const inputDepartureSites: FiltersData[] = Array.from(
        new Map(
          data.x.map((sites) => {
            const key = sites.split("/")[0];
            return [key, { key, keyDisplayed: key } as FiltersData];
          })
        ).values()
      );

      this.filterDepartureSitesInput.push(...inputDepartureSites);

      // What the URL asked for, else the first 10 unique departure sites
      this.filterDepartureSites =
        this.takeUrlSelection("dep", inputDepartureSites) ??
        inputDepartureSites.slice(0, 10).map((site) => String(site.key));
    }
    if (
      this.selectedGraphConfig?.frontConfig?.arrivalSites &&
      data.type === "bar"
    ) {
      // Extract arrival sites and remove duplicates by key
      const inputArrivalSites: FiltersData[] = Array.from(
        new Map(
          data.x.map((sites) => {
            const key = sites.split("/")[1];
            return [key, { key, keyDisplayed: key } as FiltersData];
          })
        ).values()
      );

      this.filterArrivalSitesInput.push(...inputArrivalSites);

      // What the URL asked for, else the first 10 unique arrival sites
      this.filterArrivalSites =
        this.takeUrlSelection("arr", inputArrivalSites) ??
        inputArrivalSites.slice(0, 10).map((site) => String(site.key));
    }
    if (
      this.selectedGraphConfig?.frontConfig?.packaging &&
      data.type === "bar"
    ) {
      // Extract packaging types from y_details field and remove duplicates
      const dataWithDetails = data as BarGraphDataDTO & {
        y_details?: Array<Array<{ pack: string; num: number }>>;
      };
      const packagingValues = Array.from(
        new Set(
          (dataWithDetails.y_details || [])
            .flat()
            .map((detail) => detail.pack)
            .filter((pack) => !!pack)
        )
      );

      if (packagingValues.length > 0) {
        const inputPackaging: FiltersData[] = packagingValues.map(
          (packaging) =>
            ({
              key: packaging,
              keyDisplayed: packaging
            }) as FiltersData
        );

        this.filterPackagingInput.push(...inputPackaging);

        // What the URL asked for; nothing selected by default (empty = all)
        this.filterPackaging =
          this.takeUrlSelection("pack", inputPackaging) ?? [];
      }
    }
  }

  private async updateDatas(): Promise<void> {
    this.hasData = false;
    this.hasError = false;

    this.clearFilterInputs();
    // Also clear the selected packaging values to sync with the button badge
    this.filterPackaging = [];

    if (
      this.filterGraphic &&
      (this.filterContainers?.length ||
        this.selectedGraphConfig?.requests[0] === "cacheGraphs")
    ) {
      this.loading = true;

      if (this.selectedGraphConfig?.requests?.length > 0) {
        this.datas = [];

        const reqs = this.selectedGraphConfig.requests;
        for (let i = 0; i < reqs.length; i++) {
          try {
            const data: any = await this.getDatas(reqs[i], i);
            this.datas.push(data);
            this.extractFilterInputs(data);
            this.hasData = true;
          } catch {
            this.hasError = true;
            this.notifier.error(this.errorMsg);
          }
        }
      } else {
        this.hasData = false;
        this.hasError = true;
        this.notifier.error("No requests data found.");
      }
      this.loading = false;
    }

    // Publish the effective selection: the data-derived filters only got their
    // values just now, in extractFilterInputs (the computed top-N, or what the
    // URL asked for), and the packaging reset above changed one too — before this
    // the URL could not show either.
    this.mirrorQueryFilters();
  }

  private checkCallUpdate(): void {
    const maxContainers: number | undefined =
      this.selectedGraphConfig?.frontConfig?.maxContainers;

    if (!maxContainers || this.filterContainers?.length <= maxContainers) {
      this.updateDatas();
    } else {
      this.hasData = false;
    }
  }

  /**
   * Apply client-side filters to the graph data
   * This method refreshes the graph display with the current filters
   */
  private applyClientSideFilters(): void {
    // Since we need to pass these filters to the graphs component
    // Force a refresh of the graph component by creating a new array reference
    this.datas = [...this.datas];
  }

  /**
   * The URL names a chart: select it, unless it is already the one displayed (a
   * back/forward re-emit, or the deep-link selection ngOnInit already made).
   * @param slug {string} The chart slug from the route param.
   */
  private showGraphFromSlug(slug: string): void {
    const config = this.graphFromSlug(slug);
    if (!config) {
      // Slug names no chart (typo, renamed chart, stale bookmark): say so and
      // fall back to the library view.
      this.chartRoute.notFound();
    } else if (this.filterGraphic !== config.name) {
      this.applyGraphSelection(config);
    }
  }

  public showGraphLibraryView(): void {
    // Route-driven: navigating to /analytics triggers applyLibraryView().
    this.chartRoute.toList();
  }

  public onGraphSelectedFromLibrary(graphConfig: IGraph): void {
    this.goToGraph(graphConfig.name);
  }

  /** Navigate to a chart's deep-link route (selection is driven by the route). */
  private goToGraph(name: string): void {
    const slug = ANALYTICS_GRAPH_NAME_TO_SLUG[name];
    if (slug) {
      this.chartRoute.goTo(slug);
    }
  }

  /** Set the active graph state. Clears chart-specific filters unless preserving. */
  private applyGraphSelection(config: IGraph, preserveFilters = false): void {
    this.showGraphLibrary = false;
    this.isGraphSelected = true;
    this.filterGraphic = config.name;
    this.selectedGraphConfig = config;
    if (!preserveFilters) {
      this.clearOptionalFilters();
    }
    this.clearFilterInputs();
    // After selectedGraphConfig and the clear, before the request: the URL has to
    // show this chart's filter set — and lose the previous chart's — even if the
    // request fails or returns nothing. updateDatas mirrors again once the
    // data-derived defaults are computed.
    this.mirrorQueryFilters();
    this.checkCallUpdate();
  }

  /** Reset to the graph-library view (state only; navigation is separate). */
  private applyLibraryView(): void {
    this.destroyRequestObservable.next();
    this.destroyRequestObservable = new Subject<void>();
    this.showGraphLibrary = true;
    this.isGraphSelected = false;
    this.hasData = false;
    this.loading = false;
    this.hasError = false;
    this.errorMsg = "";
    this.filterGraphic = "";
    this.selectedGraphConfig = null;
    this.datas = [];
  }

  public manageContainers(containers: string[]): void {
    // localStorage is the sync service's job now: mirrorQueryFilters() persists
    // `pkg` under filterContainers as it writes it to the URL.
    this.filterContainers = containers;
    this.mirrorQueryFilters();
    this.checkCallUpdate();
  }

  public manageDates(dates: Date[]): void {
    if (dates) {
      this.filterDateStart = dates[0];
      this.filterDateEnd = dates[1];
    } else {
      this.initDatesFilter();
    }
    this.mirrorQueryFilters();
    this.checkCallUpdate();
  }

  //* OPTIONAL FILTERS

  public manageSites(sites: string[]): void {
    this.filterSites = sites;
    if (this.hasData && this.datas) {
      // Update the graphs with client-side filtering
      this.applyClientSideFilters();
    }
    // Unconditional: the selection changed even when there is no data to redraw.
    this.mirrorQueryFilters();
  }

  public manageDepartureSites(sites: string[]): void {
    this.filterDepartureSites = sites;
    if (this.hasData && this.datas) {
      this.applyClientSideFilters();
    }
    this.mirrorQueryFilters();
  }

  public manageArrivalSites(sites: string[]): void {
    this.filterArrivalSites = sites;
    if (this.hasData && this.datas) {
      this.applyClientSideFilters();
    }
    this.mirrorQueryFilters();
  }

  public manageDays(days: number): void {
    this.filterDays = days;
    this.mirrorQueryFilters();
    this.checkCallUpdate();
  }

  public manageAlertTypes(alertType: string): void {
    this.filterAlertType = alertType;
    if (this.hasData && this.datas) {
      // Recalculate site filters based on new alert type
      this.filterSitesInput = [];
      this.filterSites = []; // Reset selected sites
      // Reset packaging filters to ensure button badge syncs correctly
      this.filterPackagingInput = [];
      this.filterPackaging = [];
      this.datas.forEach((data) => this.extractFilterInputs(data as any));
      // Create new array reference to trigger Angular change detection
      this.filterSites = [...this.filterSites];
      this.filterSitesInput = [...this.filterSitesInput];
      this.filterPackaging = [...this.filterPackaging];
      this.filterPackagingInput = [...this.filterPackagingInput];
      this.applyClientSideFilters();
    }
    // Last, not first: the branch above recomputes the site and packaging
    // selections, and the URL must carry those, not the pre-reset ones.
    this.mirrorQueryFilters();
  }

  public manageContainerCategories(category: string): void {
    this.filterContainerCategory = category;
    this.mirrorQueryFilters();
    this.checkCallUpdate();
  }

  public managePackaging(packaging: string[]): void {
    this.filterPackaging = packaging;
    if (this.hasData && this.datas) {
      this.applyClientSideFilters();
    }
    this.mirrorQueryFilters();
  }

  public onSearchTermChanged(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.filterCharts();
  }

  private filterCharts(): void {
    if (!this.searchTerm.trim()) {
      this.filteredCharts = [...this.configs];
      return;
    }

    const searchTermLower = this.searchTerm.toLowerCase();

    this.filteredCharts = this.configs.filter((chart) =>
      this.matchesSearchTerm(chart, searchTermLower)
    );
  }

  private matchesSearchTerm(chart: IGraph, searchTerm: string): boolean {
    if (chart.name.toLowerCase().includes(searchTerm)) return true;

    // Sanitize chart.name to prevent injection attacks in translation keys
    const sanitizedChartName = this.sanitizeTranslationKey(chart.name);

    const translatedTitle = this.translate.instant(
      `analytics.navigator.${sanitizedChartName}.cardTitle`
    );
    if (translatedTitle?.toLowerCase().includes(searchTerm)) return true;

    const translatedDescription = this.translate.instant(
      `analytics.navigator.${sanitizedChartName}.description`
    );
    if (translatedDescription?.toLowerCase().includes(searchTerm)) return true;

    return this.matchesTags(chart, searchTerm);
  }

  private sanitizeTranslationKey(key: string): string {
    // Allow only alphanumeric characters, hyphens, underscores, and dots
    return key.replace(/[^a-zA-Z0-9\-_.]/g, "");
  }

  private matchesTags(chart: IGraph, searchTerm: string): boolean {
    const { isHistorical, isStellantis } = chart.tags;

    if (isHistorical !== undefined) {
      const historicalTag = this.translate.instant(
        `analytics.tags.${isHistorical ? "isHistorical" : "isnotHistorical"}`
      );
      if (historicalTag?.toLowerCase().includes(searchTerm)) return true;
    }

    if (isStellantis !== undefined) {
      const stellantisTag = this.translate.instant(
        `analytics.tags.${isStellantis ? "isStellantis" : "isnotStellantis"}`
      );
      if (stellantisTag?.toLowerCase().includes(searchTerm)) return true;
    }

    if (chart.tags.isStock !== undefined) {
      const stockTag = this.translate.instant(
        `analytics.tags.${chart.tags.isStock ? "isStock" : "isnotStock"}`
      );
      if (stockTag?.toLowerCase().includes(searchTerm)) return true;
    }

    return false;
  }
}
